import React, { useMemo, useState } from 'react';

// Auslagen als Liste mit Filtern (Philipp, 07.10.2026) – statt einzelner Karten.
//  • Filter: Suche, Person, Plus/Minus, offen/erledigt, Zeitraum
//  • Summen der gefilterten Einträge: Plus, Minus, Saldo
//  • „Je Name": Einträge mit gleichem Grund/Namen zusammengefasst (Groß-/Kleinschreibung egal) – so sieht man sofort,
//    ob z.B. „Indira −1.190 / +1.190" ausgeglichen ist oder noch etwas offen steht.
const eur = (x) => (Number(x) || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const sign = (x) => (x > 0 ? 'text-success' : x < 0 ? 'text-danger' : 'text-muted');
const keyOf = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
const dDE = (d) => { const s = String(d || '').slice(0, 10); return /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : s; };

export default function ExpensesList({ expenses, onEdit, onDelete, onToggle }) {
  const [q, setQ] = useState('');
  const [person, setPerson] = useState('');
  const [dir, setDir] = useState('');       // '' | 'minus' | 'plus'
  const [status, setStatus] = useState(''); // '' | 'open' | 'done'
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [view, setView] = useState('list'); // 'list' | 'names'
  const [sort, setSort] = useState({ by: 'date', desc: true });

  const persons = useMemo(() => [...new Set(expenses.map((e) => e.from_person).filter(Boolean))].sort(), [expenses]);
  const rows = useMemo(() => {
    const qq = keyOf(q);
    const out = expenses.filter((e) => {
      const amt = Number(e.amount) || 0; const d = String(e.date || '').slice(0, 10);
      if (qq && !keyOf(`${e.reason} ${e.from_person}`).includes(qq)) return false;
      if (person && e.from_person !== person) return false;
      if (dir === 'minus' && !(amt < 0)) return false;
      if (dir === 'plus' && !(amt > 0)) return false;
      if (status === 'open' && e.completed) return false;
      if (status === 'done' && !e.completed) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
    const f = sort.desc ? -1 : 1;
    return out.sort((a, b) => {
      if (sort.by === 'amount') return f * ((Number(a.amount) || 0) - (Number(b.amount) || 0));
      if (sort.by === 'reason') return f * String(a.reason || '').localeCompare(String(b.reason || ''), 'de');
      return f * (String(a.date).localeCompare(String(b.date)) || (a.id - b.id));
    });
  }, [expenses, q, person, dir, status, from, to, sort]);

  const plus = rows.filter((e) => Number(e.amount) > 0).reduce((s, e) => s + Number(e.amount), 0);
  const minus = rows.filter((e) => Number(e.amount) < 0).reduce((s, e) => s + Number(e.amount), 0);
  const names = useMemo(() => {
    const m = new Map();
    rows.forEach((e) => {
      const k = keyOf(e.reason); const g = m.get(k) || { name: e.reason, n: 0, plus: 0, minus: 0, open: 0, last: '' };
      const a = Number(e.amount) || 0; g.n++; if (a > 0) g.plus += a; else g.minus += a; if (!e.completed) g.open++;
      if (String(e.date) > g.last) g.last = String(e.date); m.set(k, g);
    });
    return [...m.values()].map((g) => ({ ...g, saldo: Math.round((g.plus + g.minus) * 100) / 100 }))
      .sort((a, b) => (Math.abs(b.saldo) > 0.004) - (Math.abs(a.saldo) > 0.004) || Math.abs(b.saldo) - Math.abs(a.saldo) || a.name.localeCompare(b.name, 'de'));
  }, [rows]);

  const head = (by, label, cls = '') => (
    <th className={`text-nowrap ${cls}`} role="button" onClick={() => setSort({ by, desc: sort.by === by ? !sort.desc : by !== 'reason' })}>
      {label}{sort.by === by ? (sort.desc ? ' ▾' : ' ▴') : ''}
    </th>
  );
  const reset = () => { setQ(''); setPerson(''); setDir(''); setStatus(''); setFrom(''); setTo(''); };
  const filtered = q || person || dir || status || from || to;

  return (
    <div>
      {/* Filter */}
      <div className="card mb-3"><div className="card-body py-2">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-3"><input className="form-control form-control-sm" placeholder="Suche (Grund/Name) …" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          <div className="col-6 col-md-2">
            <select className="form-select form-select-sm" value={person} onChange={(e) => setPerson(e.target.value)}>
              <option value="">Alle Personen</option>{persons.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="col-6 col-md-2">
            <select className="form-select form-select-sm" value={dir} onChange={(e) => setDir(e.target.value)}>
              <option value="">Plus und Minus</option><option value="minus">nur Minus (−)</option><option value="plus">nur Plus (+)</option>
            </select>
          </div>
          <div className="col-6 col-md-1">
            <select className="form-select form-select-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Alle</option><option value="open">Offen</option><option value="done">Erledigt</option>
            </select>
          </div>
          <div className="col-6 col-md-2"><input type="date" className="form-control form-control-sm" value={from} onChange={(e) => setFrom(e.target.value)} title="von" /></div>
          <div className="col-6 col-md-2"><input type="date" className="form-control form-control-sm" value={to} onChange={(e) => setTo(e.target.value)} title="bis" /></div>
          {filtered && <div className="col-6 col-md-12 text-md-end"><button className="btn btn-link btn-sm p-0" onClick={reset}>Filter zurücksetzen</button></div>}
        </div>
      </div></div>

      {/* Summen */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <div className="border rounded px-3 py-2"><div className="small text-muted">Plus</div><div className="fw-bold text-success">{eur(plus)}</div></div>
        <div className="border rounded px-3 py-2"><div className="small text-muted">Minus</div><div className="fw-bold text-danger">{eur(minus)}</div></div>
        <div className="border rounded px-3 py-2"><div className="small text-muted">Saldo</div><div className={`fw-bold ${sign(plus + minus)}`}>{eur(plus + minus)}</div></div>
        <div className="border rounded px-3 py-2"><div className="small text-muted">Einträge</div><div className="fw-bold">{rows.length}{filtered ? ` von ${expenses.length}` : ''}</div></div>
        <div className="ms-auto btn-group btn-group-sm align-self-center">
          <button className={`btn ${view === 'list' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setView('list')}>Liste</button>
          <button className={`btn ${view === 'names' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setView('names')}>Je Name</button>
        </div>
      </div>

      {view === 'list' ? (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light"><tr>
              {head('date', 'Datum')}<th>Person</th>{head('reason', 'Grund / Name')}{head('amount', 'Betrag', 'text-end')}<th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className={e.completed ? 'text-muted' : ''}>
                  <td className="text-nowrap">{dDE(e.date)}</td>
                  <td>{e.from_person}</td>
                  <td>{e.reason}</td>
                  <td className={`text-end text-nowrap fw-bold ${sign(Number(e.amount))}`}>{eur(e.amount)}</td>
                  <td>
                    <div className="form-check form-switch mb-0">
                      <input className="form-check-input" type="checkbox" checked={!!e.completed} onChange={() => onToggle(e.id, e.completed)} />
                      <label className="form-check-label small">{e.completed ? 'erledigt' : 'offen'}</label>
                    </div>
                  </td>
                  <td className="text-end text-nowrap">
                    <button className="btn btn-sm btn-outline-primary py-0 me-1" onClick={() => onEdit(e)} title="Bearbeiten"><i className="bi bi-pencil"></i></button>
                    <button className="btn btn-sm btn-outline-danger py-0" onClick={() => onDelete(e.id)} title="Löschen"><i className="bi bi-trash"></i></button>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="text-center text-muted py-3">Keine Einträge{filtered ? ' für diesen Filter' : ''}.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light"><tr><th>Grund / Name</th><th className="text-end">Einträge</th><th className="text-end">Plus</th><th className="text-end">Minus</th><th className="text-end">Saldo</th><th>zuletzt</th></tr></thead>
            <tbody>
              {names.map((g) => (
                <tr key={keyOf(g.name)} role="button" onClick={() => { setQ(g.name); setView('list'); }} title="Einträge anzeigen">
                  <td>{g.name}{g.open > 0 && <span className="badge bg-warning text-dark ms-1">{g.open} offen</span>}</td>
                  <td className="text-end">{g.n}</td>
                  <td className="text-end text-success">{g.plus ? eur(g.plus) : ''}</td>
                  <td className="text-end text-danger">{g.minus ? eur(g.minus) : ''}</td>
                  <td className={`text-end fw-bold ${sign(g.saldo)}`}>{Math.abs(g.saldo) < 0.005 ? <span className="text-success">ausgeglichen</span> : eur(g.saldo)}</td>
                  <td className="text-nowrap small text-muted">{dDE(g.last)}</td>
                </tr>
              ))}
              {!names.length && <tr><td colSpan={6} className="text-center text-muted py-3">Keine Einträge.</td></tr>}
            </tbody>
          </table>
          <div className="small text-muted">Gleiche Namen werden zusammengefasst (Groß-/Kleinschreibung egal). Antippen zeigt die Einträge in der Liste.</div>
        </div>
      )}
    </div>
  );
}
